import { Schema } from 'effect';
import { HttpApiEndpoint, HttpApiGroup, HttpApiSchema } from 'effect/unstable/httpapi';
import {
  ApiBadRequest,
  ApiGone,
  ApiInternalServerError,
  ApiNotFound,
  ApiNotImplemented,
  ApiPayloadTooLarge,
  ApiServiceUnavailable,
  ApiUnauthorized,
  ApiUnsupportedMediaType,
  ApiUnprocessableEntity,
} from './common.js';

const UnknownRecord = Schema.Record(Schema.String, Schema.Unknown);

export const ParseFile = Schema.Struct({
  data: Schema.optionalKey(Schema.String),
  id: Schema.optionalKey(Schema.String),
  mime_type: Schema.optionalKey(Schema.String),
  mimeType: Schema.optionalKey(Schema.String),
  inlineData: Schema.optionalKey(
    Schema.Struct({
      data: Schema.optionalKey(Schema.String),
      mimeType: Schema.optionalKey(Schema.String),
      mime_type: Schema.optionalKey(Schema.String),
    }),
  ),
  inline_data: Schema.optionalKey(
    Schema.Struct({
      data: Schema.optionalKey(Schema.String),
      mimeType: Schema.optionalKey(Schema.String),
      mime_type: Schema.optionalKey(Schema.String),
    }),
  ),
});
export type ParseFile = Schema.Schema.Type<typeof ParseFile>;

export const ParseParser = Schema.Union([
  Schema.String,
  Schema.Struct({
    id: Schema.optionalKey(Schema.String),
    variant: Schema.optionalKey(Schema.String),
    options: Schema.optionalKey(UnknownRecord),
    vendor_options: Schema.optionalKey(UnknownRecord),
  }),
]);
export type ParseParser = Schema.Schema.Type<typeof ParseParser>;

export const ParsePageSelection = Schema.Union([
  Schema.String,
  Schema.Array(Schema.Number),
  Schema.Struct({
    from: Schema.optionalKey(Schema.Number),
    to: Schema.optionalKey(Schema.Number),
  }),
]);
export type ParsePageSelection = Schema.Schema.Type<typeof ParsePageSelection>;

/**
 * [A.6.5b] Core Unstructured-shaped parse input. Dynamic provider/output
 * options remain unknown records at the transport boundary; the stable file,
 * parser, page-selection, and metadata envelope is Effect-owned.
 */
export const ParseRequest = Schema.Struct({
  file: ParseFile,
  parser: Schema.optionalKey(ParseParser),
  tool: Schema.optionalKey(Schema.Unknown),
  parser_profile: Schema.optionalKey(Schema.Unknown),
  model: Schema.optionalKey(Schema.Unknown),
  prompt: Schema.optionalKey(Schema.Unknown),
  variant: Schema.optionalKey(Schema.Unknown),
  pages: Schema.optionalKey(ParsePageSelection),
  format: Schema.optionalKey(Schema.Unknown),
  output_format: Schema.optionalKey(Schema.Unknown),
  transformation: Schema.optionalKey(Schema.Unknown),
  transformations: Schema.optionalKey(Schema.Unknown),
  outputs: Schema.optionalKey(Schema.Unknown),
  publish: Schema.optionalKey(Schema.Unknown),
  metadata: Schema.optionalKey(UnknownRecord),
  options: Schema.optionalKey(UnknownRecord),
  vendor_options: Schema.optionalKey(UnknownRecord),
  schema: Schema.optionalKey(Schema.Unknown),
  file_name: Schema.optionalKey(Schema.String),
});
export type ParseRequest = Schema.Schema.Type<typeof ParseRequest>;

export const ParseJobStatus = Schema.Literals([
  'queued',
  'running',
  'idle',
  'awaiting_user_input',
  'succeeded',
  'completed',
  'completed_with_errors',
  'failed',
  'cancelled',
]);
export type ParseJobStatus = Schema.Schema.Type<typeof ParseJobStatus>;

export const ParseJobProgress = Schema.Struct({
  phase: Schema.String,
  current: Schema.Number,
  total: Schema.Number,
  pages_done: Schema.Number,
  pages_completed: Schema.Number,
  pages_failed: Schema.Number,
  pages_running: Schema.Number,
  pages_pending: Schema.Number,
  pages_total: Schema.Number,
  chunks_completed: Schema.Number,
  chunks_total: Schema.Number,
  percent: Schema.NullOr(Schema.Number),
});
export type ParseJobProgress = Schema.Schema.Type<typeof ParseJobProgress>;

/** Public async parse job returned by POST /v1/parse. */
export const ParseJob = Schema.Struct({
  object: Schema.Literal('job'),
  id: Schema.String,
  type: Schema.Literal('parse'),
  status: ParseJobStatus,
  created: Schema.optionalKey(Schema.Number),
  completed: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  livemode: Schema.optionalKey(Schema.Boolean),
  url: Schema.optionalKey(Schema.String),
  status_url: Schema.optionalKey(Schema.String),
  job_id: Schema.optionalKey(Schema.String),
  job_type: Schema.optionalKey(Schema.Literal('parse')),
  job_label: Schema.optionalKey(Schema.NullOr(Schema.String)),
  file_name: Schema.optionalKey(Schema.NullOr(Schema.String)),
  terminal: Schema.optionalKey(Schema.Boolean),
  next_poll_after_ms: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  retryable: Schema.optionalKey(Schema.Boolean),
  progress_current: Schema.optionalKey(Schema.Number),
  progress_total: Schema.optionalKey(Schema.Number),
  progress: Schema.optionalKey(ParseJobProgress),
  pages_completed: Schema.optionalKey(Schema.Number),
  pages_failed: Schema.optionalKey(Schema.Number),
  pages_running: Schema.optionalKey(Schema.Number),
  pages_pending: Schema.optionalKey(Schema.Number),
  pages_total: Schema.optionalKey(Schema.Number),
  chunks_completed: Schema.optionalKey(Schema.Number),
  chunks_total: Schema.optionalKey(Schema.Number),
  duration_ms: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  model: Schema.optionalKey(Schema.String),
  prompt_id: Schema.optionalKey(Schema.String),
  prompt_version: Schema.optionalKey(Schema.Union([Schema.String, Schema.Number])),
  confidence_kind: Schema.optionalKey(Schema.String),
  cost_usd: Schema.optionalKey(Schema.Number),
  error_code: Schema.optionalKey(Schema.NullOr(Schema.String)),
  user_message: Schema.optionalKey(Schema.NullOr(Schema.String)),
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  latest_error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  request: Schema.optionalKey(Schema.NullOr(Schema.Unknown)),
  result: Schema.NullOr(Schema.Unknown),
  metadata: Schema.optionalKey(UnknownRecord),
  created_at: Schema.optionalKey(Schema.Number),
  updated_at: Schema.optionalKey(Schema.Number),
  completed_at: Schema.optionalKey(Schema.NullOr(Schema.Number)),
  last_error: Schema.NullOr(
    Schema.Struct({
      message: Schema.String,
      code: Schema.optionalKey(Schema.String),
    }),
  ),
}).pipe(HttpApiSchema.status(202));
export type ParseJob = Schema.Schema.Type<typeof ParseJob>;

export const ParseApiGroup = HttpApiGroup.make('parse').add(
  HttpApiEndpoint.post('createParse', '/v1/parse', {
    payload: ParseRequest,
    success: ParseJob,
    error: [
      ApiBadRequest,
      ApiUnauthorized,
      ApiNotFound,
      ApiGone,
      ApiPayloadTooLarge,
      ApiUnsupportedMediaType,
      ApiUnprocessableEntity,
      ApiInternalServerError,
      ApiNotImplemented,
      ApiServiceUnavailable,
    ],
  }),
);

export const decodeParseRequest = Schema.decodeUnknownSync(ParseRequest);
export const decodeParseJob = Schema.decodeUnknownSync(ParseJob);
export const decodeParseRequestEffect = Schema.decodeUnknownEffect(ParseRequest);
export const decodeParseJobEffect = Schema.decodeUnknownEffect(ParseJob);
